import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Search,
  Edit2,
  KeyRound,
  Trash2,
  X,
  Loader2,
  UserCog,
  ShieldCheck,
  ShieldOff,
} from "lucide-react";
import { toast } from "sonner";

import AdminLayout from "@/components/admin/AdminLayout";
import { adminApi, authApi, formatApiError } from "@/lib/adminApi";

/* ============================================================
   ROLE CONFIGURATION
   ============================================================ */

const ROLES = [
  {
    key: "ADMIN",
    label: "ADMIN",
    description: "Full access",
  },
  {
    key: "PM",
    label: "PM",
    description: "View, New Entry, Reports",
  },
  {
    key: "APM",
    label: "APM",
    description: "View, New Entry, Reports",
  },
  {
    key: "FIELD_OFFICER",
    label: "FIELD OFFICER",
    description: "View, New DT Inspection",
  },
  {
    key: "CIRCLE_INCHARGE",
    label: "CIRCLE INCHARGE",
    description: "View, New DT Inspection",
  },
];

const ROLE_STYLE = {
  ADMIN: "bg-blue-500/10 text-blue-600",
  PM: "bg-purple-500/10 text-purple-600",
  APM: "bg-indigo-500/10 text-indigo-600",
  FIELD_OFFICER: "bg-amber-500/10 text-amber-700",
  CIRCLE_INCHARGE: "bg-emerald-500/10 text-emerald-700",

  // Legacy compatibility for already-existing database users.
  super_admin: "bg-blue-500/10 text-blue-600",
  admin: "bg-blue-500/10 text-blue-600",
  staff: "bg-amber-500/10 text-amber-700",
  viewer: "bg-muted text-muted-foreground",
};

function normalizeRole(role) {
  const value = String(role || "").trim().toUpperCase();

  const legacyRoles = {
    SUPER_ADMIN: "ADMIN",
    ADMIN: "ADMIN",
    PROJECT_MANAGER: "PM",
    PM: "PM",
    ASSISTANT_PROJECT_MANAGER: "APM",
    APM: "APM",
    FIELD_OFFICER: "FIELD_OFFICER",
    CIRCLE_INCHARGE: "CIRCLE_INCHARGE",
  };

  return legacyRoles[value] || value;
}

function roleLabel(role) {
  const normalized = normalizeRole(role);

  const found = ROLES.find((item) => item.key === normalized);

  if (found) return found.label;

  return String(role || "—")
    .replaceAll("_", " ")
    .toUpperCase();
}

function roleDescription(role) {
  const normalized = normalizeRole(role);
  return (
    ROLES.find((item) => item.key === normalized)?.description ||
    "Custom access"
  );
}

function getRoleStyle(role) {
  return (
    ROLE_STYLE[role] ||
    ROLE_STYLE[normalizeRole(role)] ||
    "bg-muted text-muted-foreground"
  );
}

/* ============================================================
   MAIN PAGE
   ============================================================ */

export default function UsersPage() {
  const me = authApi.getCachedUser();

  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");

  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null);
  const [pwd, setPwd] = useState(null);

  const load = async () => {
    setLoading(true);

    try {
      const params = {
        q: q.trim(),
        role,
        status,
      };

      const data = await adminApi.listUsers(params);

      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Initial load only.
    // Search button / Enter handles filtered reload.
  }, []);

  const remove = async (u) => {
    if (u.id === me?.id) {
      toast.error("You cannot delete your own account.");
      return;
    }

    const confirmed = window.confirm(
      `Delete ${u.name || u.email}? This cannot be undone.`
    );

    if (!confirmed) return;

    try {
      await adminApi.deleteUser(u.id);
      toast.success("User deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleStatus = async (u) => {
    if (u.id === me?.id) {
      toast.error("You cannot deactivate your own account.");
      return;
    }

    const next = u.status === "active" ? "inactive" : "active";

    try {
      await adminApi.updateUser(u.id, {
        status: next,
      });

      toast.success(
        next === "active" ? "User activated" : "User deactivated"
      );

      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const activeCount = useMemo(
    () => items.filter((u) => u.status === "active").length,
    [items]
  );

  const inactiveCount = useMemo(
    () => items.filter((u) => u.status !== "active").length,
    [items]
  );

  return (
    <AdminLayout
      title="User Management"
      subtitle="Add, edit, activate and manage access for all system users."
    >
      {/* ======================================================
          SUMMARY
         ====================================================== */}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <UserCog className="w-5 h-5 text-primary" />
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                Total Users
              </div>
              <div className="text-2xl font-bold">{items.length}</div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                Active
              </div>
              <div className="text-2xl font-bold">{activeCount}</div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
              <ShieldOff className="w-5 h-5 text-muted-foreground" />
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                Inactive
              </div>
              <div className="text-2xl font-bold">{inactiveCount}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ======================================================
          FILTERS
         ====================================================== */}

      <div className="rounded-2xl border border-border bg-card p-3 grid grid-cols-1 sm:grid-cols-5 gap-2 mb-4">
        <div className="sm:col-span-2 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />

          <input
            data-testid="user-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") load();
            }}
            placeholder="Search name, email, employee id…"
            className="w-full h-10 pl-9 pr-3 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
          />
        </div>

        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="h-10 px-3 rounded-lg bg-background border border-border text-sm"
        >
          <option value="all">Any role</option>

          {ROLES.map((r) => (
            <option key={r.key} value={r.key}>
              {r.label}
            </option>
          ))}
        </select>

        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-10 px-3 rounded-lg bg-background border border-border text-sm"
        >
          <option value="all">Any status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        <div className="flex gap-2">
          <button
            onClick={load}
            className="flex-1 h-10 px-3 rounded-lg border border-border text-sm hover:bg-muted"
          >
            Search
          </button>

          <button
            data-testid="new-user"
            onClick={() => setDlg({ mode: "new" })}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-foreground text-background text-sm font-semibold whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            Add User
          </button>
        </div>
      </div>

      {/* ======================================================
          DESKTOP TABLE
         ====================================================== */}

      <div className="rounded-2xl border border-border bg-card overflow-hidden hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-[10px] uppercase tracking-wider">
            <tr>
              <th className="px-3 py-3 text-left">Name</th>
              <th className="px-3 py-3 text-left">Email</th>
              <th className="px-3 py-3 text-left">Role</th>
              <th className="px-3 py-3 text-left">Department</th>
              <th className="px-3 py-3 text-left">Status</th>
              <th className="px-3 py-3 text-left">Last Login</th>
              <th className="px-3 py-3 text-right">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td
                  colSpan={7}
                  className="text-center py-8 text-muted-foreground"
                >
                  <Loader2 className="w-5 h-5 inline animate-spin" />
                </td>
              </tr>
            )}

            {!loading && items.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="text-center py-8 text-sm text-muted-foreground"
                >
                  No users match your filters
                </td>
              </tr>
            )}

            {!loading &&
              items.map((u) => (
                <tr
                  key={u.id}
                  data-testid={`user-row-${u.email}`}
                  className="border-t border-border hover:bg-muted/30"
                >
                  {/* Name */}
                  <td className="px-3 py-3">
                    <div className="font-semibold">
                      {u.name || "Unnamed User"}
                    </div>

                    {u.employee_id && (
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        ID: {u.employee_id}
                      </div>
                    )}
                  </td>

                  {/* Email */}
                  <td className="px-3 py-3">
                    <div>{u.email}</div>

                    {u.mobile && (
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {u.mobile}
                      </div>
                    )}
                  </td>

                  {/* Role */}
                  <td className="px-3 py-3">
                    <span
                      title={roleDescription(u.role)}
                      className={`inline-flex px-2 py-1 rounded-full text-[10px] font-bold uppercase ${getRoleStyle(
                        u.role
                      )}`}
                    >
                      {roleLabel(u.role)}
                    </span>
                  </td>

                  {/* Department */}
                  <td className="px-3 py-3 text-muted-foreground text-xs">
                    <div>{u.department || "—"}</div>

                    {u.designation && (
                      <div className="mt-0.5">{u.designation}</div>
                    )}
                  </td>

                  {/* Status */}
                  <td className="px-3 py-3">
                    <button
                      onClick={() => toggleStatus(u)}
                      disabled={u.id === me?.id}
                      title={
                        u.id === me?.id
                          ? "You cannot change your own status"
                          : "Click to change status"
                      }
                      className={`inline-flex px-2 py-1 rounded-full text-[10px] font-bold disabled:opacity-50 ${
                        u.status === "active"
                          ? "bg-emerald-500/10 text-emerald-600"
                          : "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]"
                      }`}
                    >
                      {u.status === "active" ? "ACTIVE" : "INACTIVE"}
                    </button>
                  </td>

                  {/* Last Login */}
                  <td className="px-3 py-3 text-xs text-muted-foreground">
                    {u.last_login
                      ? new Date(u.last_login).toLocaleString()
                      : "—"}
                  </td>

                  {/* Actions */}
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => setDlg({ mode: "edit", user: u })}
                      className="w-8 h-8 rounded-md hover:bg-muted inline-flex items-center justify-center"
                      title="Edit user"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>

                    <button
                      onClick={() => setPwd(u)}
                      className="w-8 h-8 rounded-md hover:bg-muted inline-flex items-center justify-center"
                      title="Reset password"
                    >
                      <KeyRound className="w-3.5 h-3.5" />
                    </button>

                    <button
                      onClick={() => remove(u)}
                      disabled={u.id === me?.id}
                      className="w-8 h-8 rounded-md hover:bg-[hsl(var(--destructive))]/10 hover:text-[hsl(var(--destructive))] inline-flex items-center justify-center disabled:opacity-30"
                      title={
                        u.id === me?.id
                          ? "You cannot delete yourself"
                          : "Delete user"
                      }
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* ======================================================
          MOBILE CARDS
         ====================================================== */}

      <div className="md:hidden space-y-2">
        {loading && (
          <div className="text-sm text-muted-foreground text-center py-6">
            <Loader2 className="w-5 h-5 inline animate-spin" />
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-6">
            No users
          </div>
        )}

        {!loading &&
          items.map((u) => (
            <div
              key={u.id}
              className="rounded-2xl border border-border bg-card p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">
                    {u.name || "Unnamed User"}
                  </div>

                  <div className="text-xs text-muted-foreground truncate">
                    {u.email}
                  </div>

                  {u.employee_id && (
                    <div className="text-[11px] text-muted-foreground mt-1">
                      Employee ID: {u.employee_id}
                    </div>
                  )}
                </div>

                <span
                  title={roleDescription(u.role)}
                  className={`inline-flex px-2 py-1 rounded-full text-[10px] font-bold uppercase whitespace-nowrap ${getRoleStyle(
                    u.role
                  )}`}
                >
                  {roleLabel(u.role)}
                </span>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg bg-muted/40 p-2">
                  <div className="text-[9px] uppercase font-bold text-muted-foreground">
                    Department
                  </div>
                  <div className="mt-0.5">
                    {u.department || "—"}
                  </div>
                </div>

                <div className="rounded-lg bg-muted/40 p-2">
                  <div className="text-[9px] uppercase font-bold text-muted-foreground">
                    Designation
                  </div>
                  <div className="mt-0.5">
                    {u.designation || "—"}
                  </div>
                </div>
              </div>

              <div className="mt-3 flex items-center gap-2 text-xs">
                <button
                  onClick={() => toggleStatus(u)}
                  disabled={u.id === me?.id}
                  className={`inline-flex px-2 py-1 rounded-full text-[10px] font-bold disabled:opacity-50 ${
                    u.status === "active"
                      ? "bg-emerald-500/10 text-emerald-600"
                      : "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]"
                  }`}
                >
                  {u.status === "active" ? "ACTIVE" : "INACTIVE"}
                </button>

                <span className="text-muted-foreground">
                  Last login:{" "}
                  {u.last_login
                    ? new Date(u.last_login).toLocaleString()
                    : "Never"}
                </span>
              </div>

              <div className="mt-3 flex gap-1">
                <button
                  onClick={() => setDlg({ mode: "edit", user: u })}
                  className="flex-1 text-xs py-1.5 rounded-lg border border-border hover:bg-muted inline-flex items-center justify-center gap-1"
                >
                  <Edit2 className="w-3 h-3" />
                  Edit
                </button>

                <button
                  onClick={() => setPwd(u)}
                  className="flex-1 text-xs py-1.5 rounded-lg border border-border hover:bg-muted inline-flex items-center justify-center gap-1"
                >
                  <KeyRound className="w-3 h-3" />
                  Reset
                </button>

                <button
                  onClick={() => remove(u)}
                  disabled={u.id === me?.id}
                  className="flex-1 text-xs py-1.5 rounded-lg border border-border hover:bg-[hsl(var(--destructive))]/10 hover:text-[hsl(var(--destructive))] disabled:opacity-30 inline-flex items-center justify-center gap-1"
                >
                  <Trash2 className="w-3 h-3" />
                  Delete
                </button>
              </div>
            </div>
          ))}
      </div>

      {/* ======================================================
          DIALOGS
         ====================================================== */}

      {dlg && (
        <UserDialog
          mode={dlg.mode}
          user={dlg.user}
          onClose={() => setDlg(null)}
          onSaved={() => {
            setDlg(null);
            load();
          }}
        />
      )}

      {pwd && (
        <ResetPwdDialog
          user={pwd}
          onClose={() => setPwd(null)}
          onSaved={() => setPwd(null)}
        />
      )}
    </AdminLayout>
  );
}

/* ============================================================
   USER CREATE / EDIT DIALOG
   ============================================================ */

function UserDialog({ mode, user, onClose, onSaved }) {
  const isEdit = mode === "edit";

  const [f, setF] = useState(() => ({
    email: user?.email || "",
    password: "",
    name: user?.name || "",
    role: normalizeRole(user?.role) || "PM",
    mobile: user?.mobile || "",
    employee_id: user?.employee_id || "",
    department: user?.department || "",
    designation: user?.designation || "",
    status: user?.status || "active",
  }));

  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!f.name.trim()) {
      return toast.error("Full name is required");
    }

    if (!f.email.trim()) {
      return toast.error("Email is required");
    }

    if (!isEdit && (!f.password || f.password.length < 6)) {
      return toast.error("Password must be at least 6 characters");
    }

    if (!ROLES.some((r) => r.key === f.role)) {
      return toast.error("Please select a valid role");
    }

    setBusy(true);

    try {
      if (isEdit) {
        const patch = {
          name: f.name.trim(),
          role: f.role,
          mobile: f.mobile.trim(),
          employee_id: f.employee_id.trim(),
          department: f.department.trim(),
          designation: f.designation.trim(),
          status: f.status,
        };

        await adminApi.updateUser(user.id, patch);
      } else {
        await adminApi.createUser({
          email: f.email.trim().toLowerCase(),
          password: f.password,
          name: f.name.trim(),
          role: f.role,
          mobile: f.mobile.trim(),
          employee_id: f.employee_id.trim(),
          department: f.department.trim(),
          designation: f.designation.trim(),
          status: f.status,
        });
      }

      toast.success(isEdit ? "User updated" : "User created");

      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const Field = (key, label, options = {}) => (
    <div>
      <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
        {label}
      </label>

      <input
        value={f[key] || ""}
        onChange={(e) =>
          setF((state) => ({
            ...state,
            [key]: e.target.value,
          }))
        }
        type={options.type || "text"}
        disabled={options.disabled}
        data-testid={`user-${key}`}
        className="mt-1 w-full h-10 px-3 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] disabled:opacity-60"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-card border border-border rounded-2xl overflow-hidden max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
              {isEdit ? "Edit" : "New"} User
            </div>

            <h3 className="font-heading text-lg font-bold">
              {isEdit ? user.email : "Add a new team member"}
            </h3>
          </div>

          <button
            onClick={onClose}
            className="w-9 h-9 rounded-md hover:bg-muted flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Field("name", "Full Name*")}

          {Field("email", "Email*", {
            type: "email",
            disabled: isEdit,
          })}

          {!isEdit &&
            Field("password", "Temporary Password*", {
              type: "password",
            })}

          {/* Role */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
              Role*
            </label>

            <select
              data-testid="user-role"
              value={f.role}
              onChange={(e) =>
                setF((state) => ({
                  ...state,
                  role: e.target.value,
                }))
              }
              className="mt-1 w-full h-10 px-3 rounded-lg bg-background border border-border text-sm"
            >
              {ROLES.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ))}
            </select>

            <div className="mt-1 text-[10px] text-muted-foreground">
              {roleDescription(f.role)}
            </div>
          </div>

          {Field("mobile", "Mobile")}
          {Field("employee_id", "Employee ID")}
          {Field("department", "Department")}
          {Field("designation", "Designation")}

          {/* Status */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
              Status
            </label>

            <select
              value={f.status}
              onChange={(e) =>
                setF((state) => ({
                  ...state,
                  status: e.target.value,
                }))
              }
              className="mt-1 w-full h-10 px-3 rounded-lg bg-background border border-border text-sm"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 h-10 rounded-lg border border-border text-sm"
          >
            Cancel
          </button>

          <button
            data-testid="user-save"
            onClick={save}
            disabled={busy}
            className="px-4 h-10 rounded-lg bg-foreground text-background text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   RESET PASSWORD DIALOG
   ============================================================ */

function ResetPwdDialog({ user, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!pw || pw.length < 6) {
      return toast.error("Password must be at least 6 characters");
    }

    setBusy(true);

    try {
      await adminApi.resetUserPassword(user.id, pw);

      toast.success("Password reset successfully");

      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-border">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
            Reset Password
          </div>

          <h3 className="font-heading text-lg font-bold truncate">
            {user.email}
          </h3>
        </div>

        <div className="p-5">
          <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
            New Password
          </label>

          <input
            data-testid="reset-pwd"
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            autoComplete="new-password"
            className="mt-1 w-full h-10 px-3 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
          />

          <p className="mt-2 text-xs text-muted-foreground">
            Share the temporary password with the user securely. They can
            change it after signing in.
          </p>
        </div>

        <div className="p-4 border-t border-border flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 h-10 rounded-lg border border-border text-sm"
          >
            Cancel
          </button>

          <button
            onClick={submit}
            disabled={busy}
            className="px-4 h-10 rounded-lg bg-foreground text-background text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Reset
          </button>
        </div>
      </div>
    </div>
  );
}